
import { getOutgoingMachines, getOutgoingMachinesCsv, lookupCustomer, OutgoingMachinesParams } from '@/api';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import DatePicker from '@/components/searchPage/DatePicker.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import { OutgoingMachine } from '@/models/reports';
import { Header, User } from '@/models/main';
import { notificationErrorText } from '@/utils/PortalConstants';
import { blobDownloader } from '@/utils/Common';
import * as Sentry from '@sentry/vue';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { mapState } from 'vuex';

const MAX_DATE_RANGE_DAYS = 90;

interface OutgoingMachineRow extends OutgoingMachine {
  rowKey: string;
}

@Component({
  components: {
    BackButton,
    DatePicker,
    LookupCustomerCode,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class OutgoingMachinesReport extends Vue {
  private user!: User;
  private isLoading = false;
  private isCsvLoading = false;

  private customerCode = '';
  private autocompleteCustomer = lookupCustomer;
  private serialNumber = '';

  // Date range + which date it applies to
  private dates: string[] = [];
  private datesDefault: string[] = [];
  private dateType: 'rh_job_date' | 'delivery_date' | 'completion_date' = 'rh_job_date';
  private dateTypeOptions = [
    { text: 'RH Job Date', value: 'rh_job_date' },
    { text: 'Delivery Date', value: 'delivery_date' },
    { text: 'Completion Date', value: 'completion_date' },
  ];

  // Refinement filters
  private productType: '' | 'delivery' | 'pickup' = '';
  private productTypeOptions = [
    { text: 'All', value: '' },
    { text: 'Delivery', value: 'delivery' },
    { text: 'Pickup', value: 'pickup' },
  ];
  private completedOnly = false;
  private hasSerial = false;

  private rows: OutgoingMachineRow[] = [];

  private headers: Header[] = [
    { text: 'Date', value: 'job_date', align: 'left' },
    { text: 'Job ID', value: 'rockhopper_id', align: 'left' },
    { text: 'Ref 1', value: 'reference_1', align: 'left' },
    { text: 'Ref 2', value: 'reference_2', align: 'left' },
    { text: 'Model', value: 'product_name', align: 'left' },
    { text: 'Description', value: 'product_description', align: 'left' },
    { text: 'Serial Number', value: 'serial_number', align: 'left' },
    { text: 'Suburb', value: 'suburb_name', align: 'left' },
    { text: 'State', value: 'state', align: 'left' },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
  ];

  private get rangeError(): string {
    const [from, to] = this.dates;
    if (!from || !to) {
      return '';
    }
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (toDate < fromDate) {
      return 'To date must be on or after From date.';
    }
    const diffDays = Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > MAX_DATE_RANGE_DAYS) {
      return `Date range must not exceed ${MAX_DATE_RANGE_DAYS} days.`;
    }
    return '';
  }

  private get canSearch(): boolean {
    return !!this.customerCode && !!this.dates[0] && !!this.dates[1] && !this.rangeError;
  }

  private buildParams(): OutgoingMachinesParams {
    const params: OutgoingMachinesParams = {
      customer_code: this.customerCode,
      date_from: this.dates[0],
      date_to: this.dates[1],
      date_type: this.dateType,
    };
    if (this.serialNumber) {
      params.serial_number = this.serialNumber;
    }
    if (this.productType) {
      params.product_type = this.productType;
    }
    if (this.completedOnly) {
      params.completed_only = true;
    }
    if (this.hasSerial) {
      params.has_serial = true;
    }
    return params;
  }

  private async fetchData(): Promise<void> {
    if (!this.canSearch) {
      return;
    }
    this.isLoading = true;
    const params = this.buildParams();
    await getOutgoingMachines(params)
      .then(res => {
        this.rows = res.map((row, idx) => ({
          ...row,
          rowKey: `${row.job_id}-${row.serial_number}-${idx}`,
        }));
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Outgoing Machines report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private async downloadCsv(): Promise<void> {
    if (!this.canSearch) {
      return;
    }
    this.isCsvLoading = true;
    const params = this.buildParams();
    await getOutgoingMachinesCsv(params)
      .then(res => {
        blobDownloader(res);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error downloading CSV',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isCsvLoading = false;
      });
  }

  private setCustomerCode(code: string): void {
    this.customerCode = code;
  }

  private clearCustomerCode(): void {
    this.customerCode = '';
  }

  private changeDate(dates: string[]): void {
    this.dates = dates;
  }

  private clearDateFilter(): void {
    this.dates = [...this.datesDefault];
  }

  private mounted(): void {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);
    this.datesDefault = [this.formatDate(from), this.formatDate(to)];
    this.dates = [...this.datesDefault];
    if (this.user && this.user.customerCode) {
      this.customerCode = this.user.customerCode;
    }
  }

  private formatDate(d: Date): string {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }
}
